import { InfoOutlined } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { HeatMapChartCard, PMBaseCard } from "@pimo/pimo-components";

import { RISK_HEAT_MAP_COLORS } from "../../constants";

export type RiskLevel = "Low" | "Medium" | "High";

export interface StrideThreat {
  key: string;
  label: string;
  description: string;
}

export interface DistributionChannelOption {
  id: string;
  label: string;
}

export interface RiskHeatMapCardProps {
  title: string;
  threats: StrideThreat[];
  channels: DistributionChannelOption[];
  /** `cellData[threatKey][channelId]` gives that cell's illustrative risk level. */
  cellData: Record<string, Record<string, RiskLevel>>;
}

// The chart needs a numeric `y` value per cell; these map each risk level to
// a fixed number and back so the color scale and data-label text stay in
// sync with `RISK_HEAT_MAP_COLORS` (#2413).
const RISK_LEVEL_VALUES: Record<RiskLevel, number> = {
  Low: 1,
  Medium: 2,
  High: 3,
};

const RISK_LEVEL_BY_VALUE: Record<number, RiskLevel> = {
  1: "Low",
  2: "Medium",
  3: "High",
};

const COLOR_SCALE_RANGES = (
  Object.entries(RISK_LEVEL_VALUES) as [RiskLevel, number][]
).map(([level, value]) => {
  const colors =
    RISK_HEAT_MAP_COLORS[level.toLowerCase() as "low" | "medium" | "high"];
  return {
    from: value,
    to: value,
    color: colors.background,
    foreColor: colors.text,
    name: `${level} risk`,
  };
});

// Channels are rows; each row gets a fixed height plus room for the axis,
// legend and card chrome, regardless of how many channels a tenant has.
const ROW_HEIGHT = 50;
const CHART_CHROME_HEIGHT = 110;

export const RiskHeatMapCard: PimoReactComponent<RiskHeatMapCardProps> = ({
  title,
  threats,
  channels,
  cellData,
}) => {
  const series = channels.map((channel) => ({
    name: channel.label,
    data: threats.map((threat) => ({
      x: threat.label,
      y: RISK_LEVEL_VALUES[cellData[threat.key]?.[channel.id] ?? "Low"],
    })),
  }));

  return (
    <PMBaseCard
      title={title}
      fixedWidth="100%"
      fixedHeight="auto"
      overflow="auto"
      contentPadding={3}
      rightSlotOverride={
        <Tooltip title="Illustrative risk levels per STRIDE threat category and distribution channel. Based on mock data until the SRLabs integration (#2416) is available.">
          <IconButton size="small" aria-label="Risk Heat Map info">
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <HeatMapChartCard
        title={title}
        hideCard
        series={series}
        colorScaleRanges={COLOR_SCALE_RANGES}
        dataLabelFormatter={(value) =>
          RISK_LEVEL_BY_VALUE[value] ?? String(value)
        }
        height={channels.length * ROW_HEIGHT + CHART_CHROME_HEIGHT}
      />
    </PMBaseCard>
  );
};

export default RiskHeatMapCard;
